import { apiClient } from "../../apiClient";
import { ApiResponse } from "../../../types/api";
import {
  Agent,
  AgentApiResponse,
  CreateAgentRequest,
  UpdateAgentRequest,
  GetAgentsParams,
  GetAgentsResponse,
  AgentSearchResult,
} from "./type";

interface CreateAgentBackendRequest {
  name: string;
  email: string;
  phone: string;
  agentId: string;
  commission_percent: number;
  share_percentage?: number;
  agent_type: string;
}

interface UpdateAgentBackendRequest {
  name?: string;
  email?: string;
  phone?: string;
  agentId?: string;
  commission_percent?: number;
  share_percentage?: number;
  agent_type?: string;
}

class AgentService {
  private transformAgent(apiAgent: AgentApiResponse): Agent {
    return {
      ...apiAgent,
    };
  }

  async getAgents(params: GetAgentsParams = {}): Promise<GetAgentsResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.skip !== undefined)
        queryParams.append("skip", params.skip.toString());
      if (params.limit !== undefined)
        queryParams.append("limit", params.limit.toString());
      if (params.name) queryParams.append("name", params.name);
      if (params.email) queryParams.append("email", params.email);
      if (params.phone) queryParams.append("phone", params.phone);
      if (params.agent_id) queryParams.append("agent_id", params.agent_id);
      if (params.agent_type)
        queryParams.append("agent_type", params.agent_type);

      const response = await apiClient.get<ApiResponse<GetAgentsResponse>>(
        `/agents/?${queryParams.toString()}`,
      );

      const agents = response.data.data.items.map((agent) =>
        this.transformAgent(agent),
      );

      return {
        items: agents,
        pagination: response.data.data.pagination,
        filtersApplied: response.data.data.filtersApplied,
      };
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      throw error;
    }
  }

  async getAgent(id: string): Promise<Agent> {
    try {
      const response = await apiClient.get<ApiResponse<AgentApiResponse>>(
        `/agents/${id}`,
      );

      return this.transformAgent(response.data.data);
    } catch (error) {
      console.error(`Failed to fetch agent ${id}:`, error);
      throw error;
    }
  }

  async createAgent(data: CreateAgentRequest): Promise<Agent> {
    try {
      // Transform frontend data to match backend expected format
      const requestData: CreateAgentBackendRequest = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        agentId: data.agentId,
        commission_percent: data.commissionPercent,
        share_percentage: data.sharePercentage,
        agent_type: data.agentType,
      };

      const response = await apiClient.post<ApiResponse<AgentApiResponse>>(
        "/agents/",
        requestData,
      );

      return this.transformAgent(response.data.data);
    } catch (error) {
      console.error("Failed to create agent:", error);
      throw error;
    }
  }

  async updateAgent(agentId: string, data: UpdateAgentRequest): Promise<Agent> {
    try {
      // Transform frontend data to match backend expected format
      const requestData: UpdateAgentBackendRequest = {};

      if (data.name !== undefined) requestData.name = data.name;
      if (data.email !== undefined) requestData.email = data.email;
      if (data.phone !== undefined) requestData.phone = data.phone;
      if (data.agentId !== undefined) requestData.agentId = data.agentId;
      if (data.commissionPercent !== undefined)
        requestData.commission_percent = data.commissionPercent;
      if (data.sharePercentage !== undefined)
        requestData.share_percentage = data.sharePercentage;
      if (data.agentType !== undefined) requestData.agent_type = data.agentType;

      const response = await apiClient.put<ApiResponse<AgentApiResponse>>(
        `/agents/${agentId}`,
        requestData,
      );
      return this.transformAgent(response.data.data);
    } catch (error) {
      console.error(`Failed to update agent ${agentId}:`, error);
      throw error;
    }
  }

  async deleteAgent(id: string): Promise<void> {
    try {
      await apiClient.delete(`/agents/${id}`);
    } catch (error) {
      console.error(`Failed to delete agent ${id}:`, error);
      throw error;
    }
  }

  async searchAgents(
    q: string,
    skip: number = 0,
    limit: number = 25,
  ): Promise<AgentSearchResult[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("q", q);
      queryParams.append("skip", skip.toString());
      queryParams.append("limit", limit.toString());

      const response = await apiClient.get(
        `/agents/search?${queryParams.toString()}`,
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Search failed");
      }
      return response.data.data;
    } catch (error) {
      console.error("Failed to search agents:", error);
      throw error;
    }
  }
}

export const agentService = new AgentService();

export type {
  GetAgentsParams,
  GetAgentsResponse,
  CreateAgentRequest,
  UpdateAgentRequest,
};
